import { useState, useEffect, useRef } from "react";
import { showAlertFavor, showAlertErrorMonto } from "../components/Add/Alerts";
import apiFetch from "../../../../shared/services/apiClient";

/** Redondea a 2 decimales (CRC, totales) */
const r2 = (n) => Math.round(Number(n) * 100) / 100;
/** Redondea a 4 decimales (abonos USD, para evitar residuos de TC) */
const r4 = (n) => Math.round(Number(n) * 10000) / 10000;

/**
 * useCxc — Multimoneda
 *
 * @param {number}  receiptMonto  — monto del recibo en la moneda del banco
 * @param {number}  selectedFilial
 * @param {Function} setCount
 * @param {string}  bancoMoneda   — 'CRC' | 'USD' (moneda de la cuenta bancaria seleccionada)
 * @param {number}  TC_compra     — tipo de cambio de COMPRA para ese día
 *
 * Semántica:
 *   · inv.abono está en la moneda ORIGINAL de la factura (USD para facturas USD, CRC para CRC)
 *   · El tracking interno del presupuesto se hace siempre en COLONES
 *   · restante se devuelve en CRC (el componente lo muestra en la moneda del banco si es USD)
 */
export function useCxc(
  receiptMonto,
  selectedFilial,
  setCount,
  bancoMoneda = "CRC",
  TC_compra   = 1,
) {
  const [facturasData, setFacturasData] = useState([]);
  const [restante, setRestante]         = useState(0);
  const [refreshKey, setRefreshKey]     = useState(0);
  const lastValidatedAbonoRef           = useRef({});

  /** Fuerza la recarga de las CxC de la filial seleccionada */
  function reloadCxc() {
    setRefreshKey((k) => k + 1);
  }

  /** Presupuesto total en CRC */
  function budgetCRC() {
    const tc = Number(TC_compra) || 1;
    return bancoMoneda === "USD"
      ? r2(Number(receiptMonto) * tc)
      : r2(Number(receiptMonto));
  }

  /**
   * Colones que el banco entrega de verdad por este abono.
   *
   * SIEMPRE al TC de compra: es la tasa a la que el banco mueve el dinero y la misma
   * con la que el backend mide cuánto del recibo se consumió. La brecha contra el
   * TC de venta de la factura es diferencial cambiario y la registra el asiento — no
   * debe aparecer como un "restante" que el usuario tenga que cuadrar a mano.
   *
   * Antes se usaba TC_venta en los pagos completos y TC_compra en los parciales. Esa
   * asimetría dejaba un restante imposible de cerrar que bloqueaba el guardado.
   */
  function abonoCRC(inv, abonoInvCurrency) {
    if (inv.moneda !== "USD") return r2(abonoInvCurrency);
    return r2(abonoInvCurrency * (Number(TC_compra) || 1));
  }

  /** saldo de la CxC en CRC (para mostrar en UI) */
  function saldoCRCde(inv) {
    const tc = Number(inv.tipo_cambio) || 1;
    return inv.moneda === "USD" ? r2(Number(inv.saldo) * tc) : r2(Number(inv.saldo));
  }

  // ── Cargar CxC cuando cambia la filial ───────────────────────────────
  useEffect(() => {
    if (!selectedFilial) return;
    async function fetchCxC() {
      const res = await apiFetch(
        `/api/finance/cuentaXCobrar/?filial=${selectedFilial}&all=1`,
      );
      const data    = await res.json();
      const results = Array.isArray(data) ? data : (data.results ?? []);
      const activas = results
        .map((c) => ({
          ...c,
          abono:    0,
          favor:    0,
          checked:  false,
          saldo_crc: saldoCRCde(c),
          tratar_como_diferencial: false,
          comision_bancaria: 0,
        }))
        .filter((c) => r2(c.saldo) !== 0 && c.factura != null)
        .sort((a, b) => {
          if (a.saldo < 0 && b.saldo >= 0) return -1;
          if (a.saldo >= 0 && b.saldo < 0) return 1;
          return new Date(a.fecha_factura) - new Date(b.fecha_factura) || a.id - b.id;
        });
      setFacturasData(activas);
      setCount(activas.length);
    }
    fetchCxC();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilial, setCount, refreshKey]);

  // ── Recalcular restante (en CRC) ──────────────────────────────────────
  useEffect(() => {
    // Costo de facturas normales (sin diferencial cambiario)
    let normalCost = 0;
    for (const inv of facturasData) {
      if (inv.abono > 0 && !inv.tratar_como_diferencial) {
        normalCost += inv.abono_crc_cost !== undefined
          ? inv.abono_crc_cost
          : abonoCRC(inv, inv.abono);
      }
    }
    // Crédito de saldos a favor (abono < 0)
    const creditoNegCRC = facturasData.reduce((acc, inv) => {
      if (inv.abono < 0) return acc + abonoCRC(inv, Math.abs(inv.abono));
      return acc;
    }, 0);
    // Facturas con tratar_como_diferencial consumen TODO el presupuesto restante:
    // el diferencial cambiario absorbe la diferencia entre lo que pagó el banco
    // y lo que vale la factura al TC_venta → restante = 0.
    const hasDiferencial = facturasData.some(
      (inv) => inv.abono > 0 && inv.tratar_como_diferencial,
    );
    const totalUsadoCRC = hasDiferencial
      ? budgetCRC() + creditoNegCRC   // diferencial absorbe el resto
      : normalCost;
    setRestante(r2(budgetCRC() + creditoNegCRC - totalUsadoCRC));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturasData, receiptMonto, bancoMoneda, TC_compra]);

  // ── Reset abonos cuando cambia el monto del recibo ───────────────────
  useEffect(() => {
    setFacturasData((prev) =>
      prev.map((inv) => ({ ...inv, abono: 0, favor: 0, checked: false, comision_bancaria: 0 })),
    );
  }, [receiptMonto]);

  // ── Distribuir automáticamente ───────────────────────────────────────
  function distribute() {
    let remainCRC = r2(budgetCRC());

    // Separar saldos negativos: credito USD directo vs CRC equivalente
    let remainUSD_credit   = 0;
    let crcFromUsdCredits  = 0;
    const creditoNeg = facturasData.reduce((acc, inv) => {
      // Solo cuentan como crédito los SALDOS A FAVOR reales (saldo negativo).
      // Una factura marcada con abono 0 NO es crédito — contarla inflaba el
      // presupuesto y generaba sobre-distribución (abonos mayores al monto).
      if (Number(inv.saldo) < 0 && inv.checked) {
        const crcAmt = abonoCRC(inv, Math.abs(Number(inv.saldo)));
        if (inv.moneda === "USD") {
          // Acumular el monto USD del saldo a favor para aplicarlo directo a facturas USD
          remainUSD_credit  += Math.abs(Number(inv.saldo));
          crcFromUsdCredits += crcAmt;
        }
        return acc + crcAmt;
      }
      return acc;
    }, 0);

    // CRC por cada USD de crédito (TC_venta promedio del saldo a favor)
    const crcPerUsdCredit = remainUSD_credit > 0
      ? crcFromUsdCredits / remainUSD_credit
      : (Number(TC_compra) || 1);

    remainCRC = r2(remainCRC + creditoNeg);

    if (remainCRC <= 0) {
      showAlertErrorMonto().fire();
      return;
    }

    const distributed = facturasData.map((inv, idx) => {
      const noMoney = remainCRC <= 0 && remainUSD_credit < 0.005;
      if (noMoney) return { ...inv, abono: 0, favor: 0, checked: false, abono_crc_cost: 0, comision_bancaria: 0 };
      // Saldo a favor (saldo negativo) marcado: mantenerlo aplicado con su
      // monto completo — su crédito ya fue sumado a remainCRC arriba.
      if (Number(inv.saldo) < 0 && inv.checked) {
        return { ...inv, abono: r2(Number(inv.saldo)), favor: 0, checked: true, comision_bancaria: 0 };
      }

      const saldoInv = r2(Number(inv.saldo));
      if (saldoInv <= 0) return { ...inv, abono: 0, favor: 0, checked: false, abono_crc_cost: 0, comision_bancaria: 0 };

      let abono    = 0;
      let favor    = 0;
      let crcCost  = 0;  // CRC real consumido del presupuesto (para restante exacto)
      const isLast = idx === facturasData.length - 1;

      if (inv.moneda === "USD") {
        const tcCompraInv = Number(TC_compra) || 1;

        // Paso 1: aplicar crédito USD directo (saldo a favor USD → factura USD).
        // Evita la pérdida de valor por doble conversión CRC→USD vía TC_compra.
        if (remainUSD_credit > 0.005) {
          const usdApply    = r4(Math.min(saldoInv, remainUSD_credit));
          abono            += usdApply;
          const crcForThis  = r2(usdApply * crcPerUsdCredit);
          crcCost          += crcForThis;
          remainUSD_credit  = r4(remainUSD_credit - usdApply);
          remainCRC         = r2(remainCRC - crcForThis);
        }

        // Paso 2: si la factura no quedó pagada, aplicar presupuesto CRC restante
        const saldoRem = r4(saldoInv - abono);
        if (saldoRem > 0.005 && remainCRC > 0) {
          const saldoRemCRC = r2(saldoRem * tcCompraInv);
          if (bancoMoneda === "CRC" && remainCRC >= saldoRemCRC - 0.05) {
            // El presupuesto alcanza el saldo completo valorado al TC de compra
            abono    += saldoRem;
            crcCost  += saldoRemCRC;
            remainCRC = r2(remainCRC - saldoRemCRC);
          } else {
            const inv_tc = Number(TC_compra) || 1;
            if (isLast) {
              const extra = r4(remainCRC / inv_tc);
              abono   += extra;
              crcCost += r2(remainCRC);
              if (abono > saldoInv) {
                favor = r4(abono - saldoInv);
                showAlertFavor(r2(favor), inv.moneda).fire();
              }
              remainCRC = 0;
            } else {
              const maxUSD = r4(remainCRC / inv_tc);
              const addUSD = r4(Math.min(saldoRem, maxUSD));
              abono    += addUSD;
              crcCost  += r2(addUSD * inv_tc);
              remainCRC = r2(remainCRC - addUSD * inv_tc);
            }
          }
        }
      } else {
        // Factura CRC
        if (bancoMoneda === "USD" && Number(inv.tipo_cambio) > 1) {
          // Ambos lados al mismo TC (el de compra), si no se comparan peras con manzanas.
          const remainUSD   = r2(remainCRC / (Number(TC_compra) || 1));
          const saldoInvUSD = r2(saldoInv / (Number(TC_compra) || 1));

          if (isLast) {
            if (remainUSD >= saldoInvUSD - 0.005) {
              abono = saldoInv;
            } else {
              abono = r2(remainCRC);
            }
            crcCost   = abono;
            remainCRC = 0;
          } else {
            if (remainUSD >= saldoInvUSD - 0.005) {
              abono     = saldoInv;
              crcCost   = saldoInv;
              remainCRC = r2(remainCRC - saldoInv);
            } else {
              abono     = r2(Math.min(saldoInv, remainCRC));
              crcCost   = abono;
              remainCRC = r2(remainCRC - abono);
            }
          }
        } else {
          // CRC bank + CRC invoice
          if (isLast) {
            abono = r2(remainCRC);
            if (abono > saldoInv) {
              favor = r2(abono - saldoInv);
              showAlertFavor(favor, inv.moneda).fire();
            }
            remainCRC = 0;
          } else {
            abono = r2(Math.min(saldoInv, remainCRC));
            remainCRC = r2(remainCRC - abono);
          }
          crcCost = abono;
        }
      }

      return { ...inv, abono: r4(abono), favor, checked: abono > 0, abono_crc_cost: r2(crcCost), comision_bancaria: 0 };
    });
    setFacturasData(distributed);
  }

  // ── Actualizar abono de una CxC individual ───────────────────────────
  // rawValue es en la moneda de la factura (USD o CRC)
  function updateAbono(id, rawValue) {
    setFacturasData((prev) =>
      prev.map((inv) => {
        if (inv.id !== id) return inv;

        // USD: hasta 4 decimales para evitar residuos al convertir CRC/TC_compra
        let value = inv.moneda === "USD"
          ? r4(Number(rawValue) || 0)
          : r2(Number(rawValue) || 0);
        let favor = 0;

        // Caso saldo a favor (negativo)
        if (inv.saldo < 0) return { ...inv, abono: value, favor: 0, comision_bancaria: 0 };

        const saldoInv = r2(Number(inv.saldo));

        // Calcular disponible en CRC
        const creditoNeg = prev.reduce(
          (acc, i) => acc + (Number(i.saldo) < 0 && i.checked ? abonoCRC(i, Math.abs(Number(i.saldo))) : 0),
          0,
        );
        const totalDisponibleCRC = r2(budgetCRC() + creditoNeg);
        const yaUsadoCRC = prev
          .filter((i) => i.id !== id && i.abono > 0)
          .reduce((a, i) => a + abonoCRC(i, i.abono), 0);
        const disponibleCRC = r2(Math.max(0, totalDisponibleCRC - yaUsadoCRC));

        // Convertir disponible a moneda de la factura para limitar el abono manual.
        let disponibleInvCurrency;
        if (inv.moneda === "USD") {
          if (bancoMoneda === "CRC" && Number(inv.tipo_cambio) > 1) {
            // CRC bank + USD invoice.
            // Calcular crédito USD disponible de saldos a favor USD ya seleccionados.
            const usdCreditDisponible = prev.reduce((acc, i) => {
              if (Number(i.saldo) < 0 && i.checked && i.moneda === "USD" && i.id !== id)
                return acc + Math.abs(Number(i.saldo));
              return acc;
            }, 0);
            // El límite es: crédito USD directo + presupuesto CRC convertido a USD.
            // No se capea al saldo de la factura — el exceso genera saldo a favor.
            const usdFromCRC = r4(disponibleCRC / (Number(TC_compra) || 1));
            disponibleInvCurrency = r4(usdCreditDisponible + usdFromCRC);
          } else {
            // USD bank + USD inv u otros: TC_compra
            disponibleInvCurrency = r4(disponibleCRC / (Number(TC_compra) || 1));
          }
        } else if (bancoMoneda === "USD" && Number(inv.tipo_cambio) > 1) {
          // USD bank + CRC inv: convertir presupuesto USD a CRC@TC_venta para límite
          disponibleInvCurrency = r2(
            (disponibleCRC / (Number(TC_compra) || 1)) * (Number(inv.tipo_cambio) || 1),
          );
        } else {
          disponibleInvCurrency = disponibleCRC;
        }

        if (value > disponibleInvCurrency) value = disponibleInvCurrency;
        if (value > saldoInv) {
          // r4 para USD: preservar decimales del favor (ej: $0.3937, no $0.39)
          favor = inv.moneda === "USD" ? r4(value - saldoInv) : r2(value - saldoInv);
          value = inv.moneda === "USD"
            ? r4(Math.max(value, saldoInv))
            : r2(Math.max(value, saldoInv));
          showAlertFavor(r2(favor), inv.moneda).fire();
        }

        // Calcular abono_crc_cost actualizado para que el restante sea preciso.
        // Sin esto, queda el abono_crc_cost viejo de un checkbox anterior y el
        // restante muestra un valor incorrecto aunque el abono ya haya cambiado.
        const newCrcCost = r2(abonoCRC(inv, value));

        lastValidatedAbonoRef.current[id] = value;
        return { ...inv, abono: value, favor, abono_crc_cost: newCrcCost, comision_bancaria: 0 };
      }),
    );
  }

  // ── Checkbox toggle ──────────────────────────────────────────────────
  function updateChecked(id, value) {
    let remainCRC = restante;

    // Crédito USD disponible de saldos a favor ya seleccionados
    let remainUSD_credit  = 0;
    let crcFromUsdCredits = 0;
    for (const inv of facturasData) {
      if (inv.abono < 0 && inv.checked && inv.moneda === "USD") {
        const usdAmt = Math.abs(Number(inv.saldo));
        remainUSD_credit  += usdAmt;
        crcFromUsdCredits += abonoCRC(inv, usdAmt);
      }
    }
    const crcPerUsdCredit = remainUSD_credit > 0
      ? crcFromUsdCredits / remainUSD_credit
      : (Number(TC_compra) || 1);

    const distributed = facturasData.map((inv) => {
      if (inv.id !== id) return inv;
      const saldoInv = r2(Number(inv.saldo));

      if (saldoInv < 0 && value)
        return { ...inv, abono: saldoInv, favor: 0, checked: true, negative: true, comision_bancaria: 0 };
      if (saldoInv < 0 && !value)
        return { ...inv, abono: 0, favor: 0, checked: false, negative: true, comision_bancaria: 0 };
      if (!value || (remainCRC <= 0 && remainUSD_credit < 0.005))
        return { ...inv, abono: 0, favor: 0, checked: false, negative: false, abono_crc_cost: 0, comision_bancaria: 0 };

      let abono;
      let crcCost = 0;
      if (inv.moneda === "USD") {
        const tcCompraChk = Number(TC_compra) || 1;
        const saldoCRC    = r2(saldoInv * tcCompraChk);

        // Paso 1: crédito USD directo (evita doble conversión vía TC_compra)
        if (remainUSD_credit > 0.005) {
          const usdApply   = r4(Math.min(saldoInv, remainUSD_credit));
          abono             = usdApply;
          const crcForThis  = r2(usdApply * crcPerUsdCredit);
          crcCost          += crcForThis;
          remainUSD_credit  = r4(remainUSD_credit - usdApply);
          remainCRC         = r2(remainCRC - crcForThis);
        } else if (bancoMoneda === "CRC" && remainCRC >= saldoCRC - 0.05) {
          // El presupuesto alcanza el saldo completo al TC de compra
          abono     = saldoInv;
          crcCost   = saldoCRC;
          remainCRC = r2(remainCRC - saldoCRC);
        } else {
          // Estándar: TC_compra
          const invTc = Number(TC_compra) || 1;
          abono     = r4(Math.min(saldoInv, remainCRC / invTc));
          crcCost   = r2(abono * invTc);
          remainCRC = r2(remainCRC - crcCost);
        }
      } else {
        // CRC invoice
        if (bancoMoneda === "USD" && Number(inv.tipo_cambio) > 1) {
          // USD bank + CRC invoice: usar TC_venta para verificar cobertura en USD
          // Ambos lados al mismo TC (el de compra), si no se comparan peras con manzanas.
          const remainUSD   = r2(remainCRC / (Number(TC_compra) || 1));
          const saldoInvUSD = r2(saldoInv / (Number(TC_compra) || 1));
          if (remainUSD >= saldoInvUSD - 0.005) {
            abono = saldoInv;
            remainCRC = r2(remainCRC - saldoInv);
          } else {
            abono = r2(Math.min(saldoInv, remainCRC));
            remainCRC = r2(remainCRC - abono);
          }
        } else {
          abono = r2(Math.min(saldoInv, remainCRC));
          remainCRC = r2(remainCRC - abono);
        }
        crcCost = abono;
      }
      return { ...inv, abono, favor: 0, checked: abono > 0, negative: false, abono_crc_cost: r2(crcCost), comision_bancaria: 0 };
    });

    // Auto-saldo a favor: si quedan fondos sin usar y no hay otras facturas sin
    // marcar con saldo positivo, el exceso va como saldo a favor en esta misma
    // factura (igual que el comportamiento de "isLast" en distribute).
    const hasOtherUncheckedSaldo = distributed.some(
      (inv) => inv.id !== id && Number(inv.saldo) > 0 && !inv.checked,
    );
    if (value && remainCRC > 0 && !hasOtherUncheckedSaldo) {
      const withFavor = distributed.map((inv) => {
        if (inv.id !== id || !inv.checked) return inv;
        const invTc      = Number(TC_compra) || 1;
        const extraAbono = inv.moneda === 'USD' ? r4(remainCRC / invTc) : remainCRC;
        const newAbono   = inv.moneda === 'USD'
          ? r4(inv.abono + extraAbono)
          : r2(inv.abono + extraAbono);
        const newCrcCost = r2((inv.abono_crc_cost || 0) + remainCRC);
        showAlertFavor(r2(extraAbono), inv.moneda).fire();
        remainCRC = 0;
        return { ...inv, abono: newAbono, favor: extraAbono, abono_crc_cost: newCrcCost };
      });
      setRestante(0);
      setFacturasData(withFavor);
    } else {
      setRestante(r2(Math.max(0, remainCRC)));
      setFacturasData(distributed);
    }
  }

  /**
   * Alterna el flag tratar_como_diferencial para una factura.
   * Al activarlo: el abono queda capeado al saldo de la factura (sin saldo a favor).
   * Al desactivarlo: solo limpia el flag; el usuario puede desmarcar y remarcar
   * la factura para restaurar el comportamiento automático de saldo a favor.
   */
  function toggleDiferencial(id) {
    setFacturasData((prev) => {
      const target = prev.find((i) => i.id === id);
      if (!target) return prev;
      const activating = !target.tratar_como_diferencial;

      // Costo de otras facturas positivas (para calcular el presupuesto residual)
      const otherCost = prev
        .filter((i) => i.id !== id && i.abono > 0 && !i.tratar_como_diferencial)
        .reduce(
          (acc, i) =>
            acc +
            (i.abono_crc_cost !== undefined
              ? i.abono_crc_cost
              : abonoCRC(i, i.abono)),
          0,
        );
      // Crédito de saldos a favor (abono < 0)
      const creditoNeg = prev
        .filter((i) => i.abono < 0)
        .reduce((acc, i) => acc + abonoCRC(i, Math.abs(Number(i.abono))), 0);

      return prev.map((inv) => {
        if (inv.id !== id) return inv;
        const saldoInv =
          inv.moneda === "USD" ? r4(Number(inv.saldo)) : r2(Number(inv.saldo));
        const newAbono =
          inv.moneda === "USD"
            ? r4(Math.min(inv.abono, saldoInv))
            : r2(Math.min(inv.abono, saldoInv));

        if (activating) {
          // El diferencial absorbe TODO el presupuesto restante tras otras facturas.
          // abono_crc_cost = fullBudget - otherCost → en el useEffect restante = 0.
          const newCrcCost = r2(budgetCRC() + creditoNeg - otherCost);
          return {
            ...inv,
            tratar_como_diferencial: true,
            abono: newAbono,
            favor: 0,
            abono_crc_cost: newCrcCost,
            comision_bancaria: 0,
          };
        } else {
          // Desactivar: volver al costo normal (TC_compra/TC_venta ajustado)
          const newCrcCost = r2(abonoCRC(inv, newAbono));
          return {
            ...inv,
            tratar_como_diferencial: false,
            abono: newAbono,
            favor: 0,
            abono_crc_cost: newCrcCost,
          };
        }
      });
    });
  }

  /**
   * Alterna el flag de Comisión Bancaria para una factura (cualquier método de
   * pago: tarjeta, transferencia internacional, cheque). Al activarlo: la
   * diferencia entre el saldo de la factura y el
   * abono actual se registra como comisión que el banco retuvo — la factura
   * queda cancelada por el total aunque el banco solo reciba el abono (no
   * consume presupuesto del recibo, a diferencia del abono normal).
   * Al desactivarlo: solo limpia el monto de comisión.
   */
  function toggleComisionBancaria(id) {
    setFacturasData((prev) =>
      prev.map((inv) => {
        if (inv.id !== id) return inv;
        const activating = !(Number(inv.comision_bancaria) > 0);
        if (!activating) {
          return { ...inv, comision_bancaria: 0 };
        }
        const saldoInv = inv.moneda === "USD" ? r4(Number(inv.saldo)) : r2(Number(inv.saldo));
        const gap = inv.moneda === "USD"
          ? r4(saldoInv - Number(inv.abono || 0))
          : r2(saldoInv - Number(inv.abono || 0));
        if (gap <= 0) return inv;
        return {
          ...inv,
          comision_bancaria: gap,
          favor: 0,
          tratar_como_diferencial: false,
        };
      }),
    );
  }

  /**
   * Acumula varios saldos a favor (CxC con saldo < 0) en una sola factura.
   * @param {number}        targetId   — id de la CxC que conserva el saldo combinado
   * @param {Array<number>} sourceIds  — ids de las CxC cuyo crédito se mueve al target
   * @returns {Promise<object>} respuesta del backend (CxC destino actualizada)
   */
  async function acumularSaldosFavor(targetId, sourceIds) {
    const res = await apiFetch("/api/finance/cuentaXCobrar/acumular-saldos-favor/", {
      method: "POST",
      body: JSON.stringify({ target: targetId, sources: sourceIds }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Error al acumular saldos a favor");
    }
    const data = await res.json();
    reloadCxc();
    return data;
  }

  function montoEsValido() {
    if (receiptMonto > 0) return true;
    return facturasData.some((f) => f.checked && Number(f.abono) < 0);
  }

  const negativaSeleccionada = facturasData.find(
    (f) => f.checked && Number(f.abono) < 0,
  );
  const soloSaldoFavor = Number(receiptMonto) === 0 && !!negativaSeleccionada;
  const referenciaAutoSaldo = soloSaldoFavor
    ? negativaSeleccionada.numero_recibo_origen
      ? `Aplicación saldo a favor — Recibo ${negativaSeleccionada.numero_recibo_origen}`
      : negativaSeleccionada.concepto || "Aplicación saldo a favor"
    : null;

  return {
    facturasData,
    distribute,
    updateAbono,
    updateChecked,
    toggleDiferencial,
    toggleComisionBancaria,
    acumularSaldosFavor,
    reloadCxc,
    setFacturasData,
    selectedFilial,
    restante,
    lastValidatedAbonoRef,
    montoEsValido,
    soloSaldoFavor,
    referenciaAutoSaldo,
    bancoMoneda,
    TC_compra,
  };
}
